import { DataManager } from "../model/DataManager.js";
import { VOBlog } from "../model/DataStructure.js";
import { DateUtil } from "../model/DateUtil.js";
import { Geom } from "../model/Geom.js";
import { Label } from "../model/Label.js";
import { Language, Translation } from "../model/Language.js";
import { BaseElement, BaseElementOptions } from "./BaseElement.js";
import { BlogItem } from "./BlogItem.js";

export interface BlogOptions extends BaseElementOptions {
    data: DataManager;
    date: Date;
    likesServiceUrl: string;
    blog?: VOBlog;
    assetsUrl: string;
}

export type ScrollContext = "wheel" | "touch" | "pointer" | "resize" | "script" | "none" | "keyboard";

export class Blog extends BaseElement {

    public onScroll = e5.Signal.create();
    public onChange = e5.Signal.create<[Date]>();

    public readonly element: HTMLDivElement;

    private _items: BlogItem[] = [];
    private _days: BlogDay[] = [];
    private _current: BlogItem = null;
    private _triggerChange: boolean = true;
    private _scrollContext: ScrollContext = "none";
    private _scrollContextTime: number = 0;

    public create(options: BlogOptions): this {

        super.create(options);

        this.setElement(this.createElement());

        var data = options.data;

        var current: Date = null;
        var day: BlogDay = null;
        let index = 0;
        for (var blog of data.data.awi_blog) {

            if (!current
                || DateUtil.compare(blog.date, current) !== 0) {
                day = new BlogDay(blog);
                this.element.appendChild(day.element);
                this._days.push(day);
            }

            var item = new BlogItem({
                data: blog,
                likesServiceUrl: options.likesServiceUrl,
                day: day,
                index: index++
            });
            current = blog.date;
            this._items.push(item);
            day.add(item);
        }

        var footer = document.createElement("div");
        footer.className = "awi-blog__bottom";

        var awiLink = document.createElement("a");
        awiLink.href = "https://awi.de";
        awiLink.target = "_blank";
        window.lang.set(awiLink,
            {
                type: "@href",
                de: "https://awi.de",
                en: "https://awi.de/en/"
            },
            {
                type: "@aria-label",
                de: "Link zur Webseite des Alfred-Wegener-Instituts",
                en: "Link to the website of the Alfred Wegener Institute"
            });
        awiLink.innerHTML = `<img class="awi-blog__bottom-image" src="${options.assetsUrl}awi-logo.svg" alt="AWI Logo"/>`;
        footer.appendChild(awiLink);
        this.element.appendChild(footer);

        var listenerOptions: boolean | AddEventListenerOptions = e5.Caps.passiveEventLister ? { passive: true, capture: true } : true;

        this.element.addEventListener("wheel", () => {
            this.killScrollToTween();
        }, listenerOptions);

        this.element.addEventListener("touchmove", () => {
            this.killScrollToTween();
        }, listenerOptions);

        // this.element.addEventListener("wheel", () => {
        //     this.updateScrollContext("wheel");
        // }, listenerOptions);

        // this.element.addEventListener("touchmove", () => {
        //     this.updateScrollContext("touch");
        // }, listenerOptions);

        // this.element.addEventListener("pointermove", () => {
        //     this.updateScrollContext("pointer");
        // }, listenerOptions);

        // document.addEventListener("keydown", (e) => {
        //     if (e5.KeyCode.is(e,
        //         e5.KeyCode.DOWN_ARROW,
        //         e5.KeyCode.UP_ARROW)) {
        //         this.updateScrollContext("keyboard");
        //     }
        // }, listenerOptions);

        window.addEventListener("resize", () => {
            this.updateScrollContext("resize");
        }, listenerOptions);

        this.element.addEventListener("scroll", (e) => {
            var ctx = this.getScrollContext();

            // console.log("SCROLL BY", ctx);
            var changedScrollItem = this.updateScrollItem();
            // switch (ctx) {
            //     case "pointer":
            //     case "touch":
            //     case "wheel":
            //     case "keyboard":
            //         {
            //             gs.TweenMax.killTweensOf(this.element);
            //             this._triggerChange = true;
            //             var changedScrollItem = this.updateScrollItem();
            //             if (changedScrollItem) {
            //                 this.onScroll.dispatch();
            //             }
            //             break;
            //         }
            //     default: {
            //         this._current = null;
            //     }
            // }

            if (ctx !== "script"
                && ctx !== "resize") {
                gsap.killTweensOf(this.element);
                this._triggerChange = true;

                if (changedScrollItem) {
                    // console.log("SCROLL", ctx);
                    this.onScroll.dispatch();
                }
            } else {
                // this._current = null;
            }

        }, listenerOptions);

        this.onScroll.debounce(() => {
            var item = this.getItemByScroll();
            if (item) {
                // console.log("BLOCK CHANGE ITEM", item.data.title_de);
                if (this._triggerChange) {
                    this.onChange.dispatch(new Date(item.data.date));
                }
            }
        }, 500);

        if (this._items.length === 0) {
            this.setEmpty();
        } else {
            if (options.blog) {
                this.setBlog(options.blog, 0, false);
            } else {
                this.setDate(options.date, 0, false);
            }
        }


        e5.event.on(this.element, "keydown", `div[role="button"]`, (e) => {

            if (e.event.repeat) {
                return;
            }
            if (e5.KeyCode.is(e.event,
                e5.KeyCode.ENTER,
                e5.KeyCode.SPACE)) {
                e.target.click();
                e.event.preventDefault();
                e.event.stopImmediatePropagation();
            }
        })

        return this;
    }

    private setEmpty(): void {
        this.element.classList.add("awi-blog--empty");


        var emptyContainer = document.createElement("div");
        emptyContainer.className = "awi-blog__empty-container";
        this.element.appendChild(emptyContainer);

        var empty = document.createElement("div");
        // empty.className = "awi-blog__empty-icon fal fa-exclamation-triangle";
        // empty.className = "awi-blog__empty-icon fal fa-empty-set";
        empty.className = "awi-blog__empty-icon fal fa-folder-open";
        // empty.className = "awi-blog__empty-icon fal fa-tasks";
        emptyContainer.appendChild(empty);


        var text = document.createElement("div");
        text.className = "awi-blog__empty-text";
        Label.set(text, "main.blog.empty");
        emptyContainer.appendChild(text);
    }

    public resize(): void {
        if (this._current) {
            // if (this._current != this.getItemByScroll()) {
            this._scrollContext = "script";


            this.element.scrollTop = this._current.offsetTop;

            setTimeout(() => {
                // this.element.scrollTop = this._current.offsetTop;
            });
            // }
        }
    }

    protected createElement(): HTMLDivElement {
        var e = document.createElement("div");
        e.className = "awi-blog awi-scrollbar awi-scrollbar--vert";
        return e;
    }

    private updateScrollItem(): boolean {
        var item = this.getItemByScroll();
        var changed = item !== this._current;
        this._current = item;
        return changed;
    }

    public setBlog(blog: VOBlog, time: number | "auto" = "auto", triggerChange: boolean = true): void {

        if (!blog
            || this._items.length === 0) {
            return;
        }

        var blogItem = this.getItem(blog);
        if (!blogItem) {
            return;
        }

        this.scrollTo(blogItem.offsetTop, time, triggerChange);
    }

    public setDate(date: Date, time: number | "auto" = "auto", triggerChange: boolean = true): void {

        if (this._days.length === 0) {
            return;
        }

        var target: BlogDay = null;
        for (var item of this._days) {
            if (DateUtil.smallerEqual(item.data.date, date)) {
                target = item;
                break;
            }
        }

        if (!target) {
            if (DateUtil.smaller(date, this._days[0].data.date)) {
                target = this._days[0];
            } else {
                target = this._days[this._days.length - 1];
            }
        }

        if (!target) {
            return;
        }

        var offsetTop = item.element.offsetTop;


        this.scrollTo(offsetTop, time, triggerChange);
    }

    private _scrollToTween: gsap.core.Tween = null;

    private killScrollToTween(): void {
        if (this._scrollToTween) {
            this._scrollToTween.kill();
            this._scrollToTween = null;
        }
    }

    private scrollTo(offsetTop: number, time: number | "auto" = "auto", triggerChange: boolean = true): void {

        this.killScrollToTween();

        var duration = time === "auto" ? 0.5 : time;
        this.updateScrollContext("script");
        this._triggerChange = triggerChange;
        this._scrollToTween = gsap.to(this.element, {
            duration: duration,
            scrollTop: offsetTop,
            onUpdate: () => {
                this.updateScrollContext("script");
            },
            onComplete: () => {
                this._triggerChange = true;
            },
            onOverwrite: () => {
                this._triggerChange = true;
            }
        });
    }

    private getItem(blog: VOBlog): BlogItem {
        for (var item of this._items) {
            if (item.data === blog) {
                return item;
            }
        }
        return null;
    }

    private getScrollContext(): ScrollContext {
        var delta = Date.now() - this._scrollContextTime;
        if (delta < 300) {
            return this._scrollContext;
        }
        return "none";
    }

    private getItemByScroll(): BlogItem {
        var offset = this.element.scrollTop;

        for (var item of this._items) {
            var top = item.offsetTop - 10;
            var bottom = top + item.offsetHeight;
            if (offset >= top
                && offset <= bottom) {
                return item;
            }
        }

        if (offset <= 0) {
            return this._items[0];
        }
        return this._items[this._items.length - 1];
    }

    private updateScrollContext(ctx: ScrollContext) {
        // gs.TweenMax.killTweensOf(this.element);
        this._scrollContext = ctx;
        this._scrollContextTime = Date.now();
    }
}

export class BlogDay {
    public readonly element: HTMLDivElement;
    public readonly items: BlogItem[] = [];

    constructor(public readonly data: VOBlog) {
        this.element = document.createElement("div");
        this.element.className = "awi-blog__day-container";

        var date = document.createElement("div");
        date.className = "awi-blog__date";
        date.innerHTML = `
        <span class="awi-blog__subtitle-date"></span>
        <span class="awi-blog__subtitle-separator" aria-hidden="true"></span>
        <span class="awi-blog__subtitle-coordinate" aria-hidden="true"></span>
        <span class="awi-blog__day-corner" aria-hidden="true"></span>
        `;

        this.element.appendChild(date);

        lang.set(
            date.querySelector(".awi-blog__subtitle-date"),
            {
                de: DateUtil.toStringReadable(data.date, "de"),
                en: DateUtil.toStringReadable(data.date, "en"),
            });

        // <span class="awi-blog__subtitle-days"></span>
        // <span class="awi-blog__subtitle-separator"></span>
        // lang.set(
        //     date.querySelector(".awi-blog__subtitle-days"),
        //     {
        //         de: "Tag " + data.days,
        //         en: "Days " + data.days
        //     });

        if (data.day.position) {
            lang.set(
                date.querySelector(".awi-blog__subtitle-coordinate"),
                {
                    de: Geom.ddToDms(data.day.position.latitude, data.day.position.longitude)
                });
        } else {
            date.querySelector(".awi-blog__subtitle-separator").remove();
        }
    }

    public add(item: BlogItem): void {
        this.items.push(item);
        this.element.appendChild(item.element);
    }
}